import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, IndianRupee, MapPin, Calendar, Clock, CheckCircle, XCircle, Timer } from 'lucide-react';

import { BACKEND_URL as API } from '../../utils/apiConfig';

let cashfreeInstance = null;
const loadCashfree = async () => {
  if (cashfreeInstance) return cashfreeInstance;
  try {
    const { load } = await import('@cashfreepayments/cashfree-js');
    cashfreeInstance = await load({ mode: 'production' });
    return cashfreeInstance;
  } catch (e) {
    console.error('Cashfree load error:', e);
    return null;
  }
};

export default function WhatsAppPayPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setError('Missing booking ID');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/api/payments/booking-details/${bookingId}`);
        const data = res.data;
        setBooking(data);

        if (data.status === 'Confirmed') {
          setError('already_paid');
        } else if (data.status === 'Expired' || data.status === 'Cancelled') {
          setError('expired');
        } else if (data.expires_at) {
          const expiryTime = new Date(data.expires_at).getTime();
          const now = Date.now();
          if (expiryTime <= now) {
            setError('expired');
          } else {
            setTimeLeft(Math.floor((expiryTime - now) / 1000));
          }
        }
      } catch (e) {
        setError('Booking not found');
      }
      setLoading(false);
    };
    fetchBooking();
  }, [bookingId]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setError('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handlePay = async () => {
    if (!booking) return;
    setPaymentStarted(true);
    try {
      // Use the SAME create-order endpoint as the app
      const response = await axios.post(`${API}/api/payments/create-order`, {
        booking_id: bookingId,
        amount: parseFloat(booking.final_amount || booking.amount || 0),
        customer_name: booking.player_name || 'Player',
        customer_phone: booking.player_mobile || '',
        customer_email: ''
      });

      if (!response.data.success) {
        setError(response.data.error || 'Failed to create payment');
        setPaymentStarted(false);
        return;
      }

      const cashfree = await loadCashfree();
      if (!cashfree) {
        setError('Payment gateway failed to load. Please try again.');
        setPaymentStarted(false);
        return;
      }

      await cashfree.checkout({
        paymentSessionId: response.data.payment_session_id,
        redirectTarget: '_self',
      });
    } catch (e) {
      console.error('Payment error:', e);
      setError(e.response?.data?.detail || 'Payment failed. Please try again.');
      setPaymentStarted(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (error === 'already_paid') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-6">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Already Paid</h2>
          <p className="text-slate-500">This booking has already been confirmed.</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-6">
        <div className="text-center">
          <Timer className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Booking Expired</h2>
          <p className="text-slate-500">This booking has expired. Please create a new booking via WhatsApp.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-6">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Payment Error</h2>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <div className="max-w-md mx-auto p-6 pt-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="pay-heading">MAKS Sports</h1>
          <p className="text-sm text-slate-500 mt-1">Complete your booking payment</p>
        </div>

        {/* Timer */}
        {timeLeft !== null && timeLeft > 0 && (
          <div className={`text-center mb-4 py-2 px-4 rounded-xl text-sm font-medium ${
            timeLeft < 300 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
          }`} data-testid="payment-timer">
            <Timer className="w-4 h-4 inline mr-1" />
            Pay within {formatTime(timeLeft)} to secure your booking
          </div>
        )}

        {/* Booking Details */}
        <div className="bg-white rounded-2xl shadow-lg border border-violet-100 p-5 mb-6">
          <h3 className="font-semibold text-slate-700 mb-4 text-sm uppercase tracking-wide">Booking Details</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Venue</p>
                <p className="font-medium text-slate-800">{booking.venue || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Date & Game</p>
                <p className="font-medium text-slate-800">{booking.date || '—'} &bull; {booking.game || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Time & Court</p>
                <p className="font-medium text-slate-800">
                  {booking.start_time || '—'} - {booking.end_time || '—'}
                  {booking.courts?.length > 1 ? ` (${booking.courts.join(', ')})` : booking.court ? ` (${booking.court})` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Total Amount</span>
              <span className="text-2xl font-bold text-slate-900 flex items-center">
                <IndianRupee className="w-5 h-5" />
                {booking.final_amount || booking.amount || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Pay Button */}
        <button
          onClick={handlePay}
          disabled={paymentStarted}
          data-testid="wa-pay-button"
          className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold text-lg shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all disabled:opacity-60"
        >
          {paymentStarted ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              Pay <IndianRupee className="w-5 h-5" />{booking.final_amount || booking.amount || 0}
            </span>
          )}
        </button>

        <p className="text-center text-xs text-slate-400 mt-4">
          Secured by Cashfree Payments
        </p>
      </div>
    </div>
  );
}
